import React from "react";
import useLegalInfo from "@/hooks/useLegalInfo";
import { LegalLayout, Section, KV } from "@/components/LegalLayout";
import { PRIVACY_POLICY_VERSION } from "@/lib/consent";

export default function PrivacyPolicy() {
  const { L, info } = useLegalInfo();
  return (
    <LegalLayout kicker={`Versión ${PRIVACY_POLICY_VERSION}`} title="Política de Privacidad" updated="junio 2026" testid="privacy-policy-page">
      <p>
        En cumplimiento del Reglamento (UE) 2016/679 General de Protección de Datos (RGPD) y de la Ley Orgánica 3/2018 de
        Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD), te informamos de cómo tratamos tus datos personales.
      </p>

      <Section title="1. Responsable del tratamiento">
        <KV rows={[
          ["Titular", L("company_name")],
          ["NIF/CIF", L("tax_id")],
          ["Domicilio", L("address")],
          ["Email de contacto", L("email")],
          ["Delegado de Protección de Datos", info.dpo_email || "No designado"],
        ]} />
      </Section>

      <Section title="2. Datos que tratamos">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Datos de cuenta:</strong> nombre, email, contraseña (cifrada), teléfono, nombre del negocio, logo e IBAN.</li>
          <li><strong>Datos de tus clientes:</strong> los que introduces para emitir presupuestos y facturas (nombre, NIF, dirección, contacto). Respecto a ellos actuamos como <em>encargado del tratamiento</em> por tu cuenta.</li>
          <li><strong>Datos económicos:</strong> presupuestos, facturas, gastos y tickets escaneados.</li>
          <li><strong>Datos de pago:</strong> gestionados por Stripe; no almacenamos los datos completos de tu tarjeta.</li>
          <li><strong>Datos técnicos:</strong> IP, fecha y versión de los consentimientos prestados, y, solo si lo aceptas, datos de navegación analíticos.</li>
        </ul>
      </Section>

      <Section title="3. Finalidades y base jurídica">
        <KV rows={[
          ["Prestar el servicio y gestionar tu cuenta", "Ejecución del contrato (art. 6.1.b RGPD)"],
          ["Facturación, contabilidad y Verifactu", "Obligación legal (art. 6.1.c RGPD; normativa fiscal y mercantil)"],
          ["Responder a solicitudes de información", "Consentimiento (art. 6.1.a RGPD)"],
          ["Analítica web y mejora del producto", "Consentimiento mediante el panel de cookies"],
          ["Seguridad y prevención del fraude", "Interés legítimo (art. 6.1.f RGPD)"],
        ]} />
      </Section>

      <Section title="4. Plazo de conservación">
        <p>Conservamos tus datos mientras mantengas la cuenta activa. Al eliminarla, borramos tus datos de forma inmediata, salvo:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Las <strong>facturas emitidas y sus registros Verifactu</strong>, que se desvinculan de tu cuenta y se mantienen <strong>bloqueados</strong> durante el plazo legal (hasta 6 años, art. 30 Código de Comercio y Ley General Tributaria), accesibles solo para atender requerimientos de autoridades.</li>
          <li>Las pruebas de consentimiento, anonimizadas, para acreditar el cumplimiento normativo.</li>
        </ul>
      </Section>

      <Section title="5. Destinatarios y encargados">
        <p>No cedemos tus datos a terceros salvo obligación legal (p. ej. Agencia Tributaria). Utilizamos proveedores que actúan como encargados del tratamiento:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Alojamiento y base de datos en la nube.</li>
          <li>Stripe Payments Europe Ltd. (pagos de suscripción).</li>
          <li>Google (Gemini) para generar presupuestos y leer tickets con IA.</li>
          <li>Proveedor de email transaccional.</li>
          <li>PostHog (analítica), solo si aceptas las cookies analíticas.</li>
        </ul>
        <p>Algunos proveedores pueden estar fuera del EEE; en ese caso las transferencias se amparan en Cláusulas Contractuales Tipo de la Comisión Europea o en el Marco de Privacidad de Datos UE-EE. UU.</p>
      </Section>

      <Section title="6. Tus derechos">
        <p>Puedes ejercer en cualquier momento tus derechos de <strong>acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad</strong>, así como retirar tu consentimiento, escribiendo a {L("email")} con copia de tu documento identificativo.</p>
        <p>Desde <strong>Mi perfil → Privacidad</strong> puedes descargar todos tus datos (portabilidad) y eliminar tu cuenta sin necesidad de contactarnos.</p>
        <p>Si consideras que no hemos tratado tus datos correctamente, puedes reclamar ante la Agencia Española de Protección de Datos (<a className="text-orange-600 underline" href="https://www.aepd.es" target="_blank" rel="noreferrer">www.aepd.es</a>).</p>
      </Section>

      <Section title="7. Seguridad">
        <p>Aplicamos medidas técnicas y organizativas adecuadas: cifrado en tránsito (HTTPS), contraseñas con hash bcrypt, control de accesos y copias de seguridad.</p>
      </Section>
    </LegalLayout>
  );
}
